import React, { useCallback } from 'react';
import { Button, ButtonProps } from '@material-ui/core';
import PhoneIcon from '@material-ui/icons/Phone';
import { useTranslate } from 'hooks/use-translate';
import { useCurrentUser } from 'components/hooks';
import { useEffectError } from 'hooks';
import { apiVoipe } from 'services/voipe-services';
import { Unset } from 'utils/types';
import { isMutationRejected } from 'utils/rtk-query';
import { apiTodoTaskActivities } from 'services/todo-task-activities';
import { apiSupportMeetingActivities } from 'services/support-meeting-activities';
import { apiClinicalMeetingActivities } from 'services/clinical-meeting-activities';

const useCallMutation = apiVoipe.useVoipeCallToPatientMutation;
const usePostTaskActivityMutation = apiTodoTaskActivities.usePostTodoTaskMeetingActivityMutation;
const usePostSupportMeetingActivityMutation =
  apiSupportMeetingActivities.usePostSupportMeetingActivityMutation;
const usePostClinicalMeetingActivityMutation =
  apiClinicalMeetingActivities.usePostClinicalMeetingActivityMutation;

interface Props extends ButtonProps {
  userPatientProfileID: string;
  taskID: Unset;
  supportMeetingID: Unset;
  clinicalMeetingID: Unset;
  onDone?: () => void;
}

export const ButtonCall: React.FC<Props> = ({
  userPatientProfileID,
  taskID,
  supportMeetingID,
  clinicalMeetingID,
  onDone,
  ...rest
}) => {
  const { tp } = useTranslate();
  const { phoneExtension } = useCurrentUser();

  const [triggerCall, resultCall] = useCallMutation();
  useEffectError(resultCall.error);

  const [triggerTaskActivity, resultTaskActivity] = usePostTaskActivityMutation();
  useEffectError(resultTaskActivity.error);

  const [triggerSupportMeetingActivity, resultSupportMeetingActivity] =
    usePostSupportMeetingActivityMutation();
  useEffectError(resultSupportMeetingActivity.error);

  const [triggerClinicalMeetingActivity, resultClinicalMeetingActivity] =
    usePostClinicalMeetingActivityMutation();
  useEffectError(resultClinicalMeetingActivity.error);

  const isLoading = resultCall.isLoading;

  const onClick = useCallback(async () => {
    const result = await triggerCall({ userPatientProfileID });

    if (isMutationRejected(result)) {
      return;
    }

    if (taskID) {
      await triggerTaskActivity({
        toDoTaskID: taskID,
        remarks: tp('activity-to-do-task-call-to-patient'),
      });
    }

    if (supportMeetingID) {
      await triggerSupportMeetingActivity({
        supportMeetingID,
        remarks: tp('activity-support-meeting-call-to-patient'),
      });
    }

    if (clinicalMeetingID) {
      await triggerClinicalMeetingActivity({
        clinicalMeetingID,
        remarks: tp('activity-clinical-meeting-call-to-patient'),
        remarkForPatientCallStatusID: undefined,
        changes: undefined,
      });
    }

    onDone && onDone();
  }, [
    triggerCall,
    triggerTaskActivity,
    triggerSupportMeetingActivity,
    triggerClinicalMeetingActivity,
    userPatientProfileID,
    onDone,
    taskID,
    tp,
    supportMeetingID,
    clinicalMeetingID,
  ]);

  return (
    <Button
      variant={'contained'}
      color={'primary'}
      startIcon={<PhoneIcon />}
      disabled={!phoneExtension || isLoading}
      onClick={onClick}
      {...rest}
    >
      {tp('call-to-patient')}
    </Button>
  );
};
